import Promise from 'bluebird';
import { push } from 'react-router-redux';
import { CHANGE } from 'redux-form/lib/actionTypes';
import { createAudioContext, nextFrame } from '../helpers/serverRenderingUtils';
import Exporter from './export';
import {
  addSegment,
  clearSegments,
  updateBuffer,
  trimAndFade,
  updateTitle,
  setSubmitted,
} from './clipSelection';

// constants

export const CLEAR_ERROR = 'CLEAR_ERROR';
export const RECEIVE_CONTEXT = 'RECEIVE_CONTEXT';
export const RECEIVE_ERROR = 'RECEIVE_ERROR';
export const RECEIVE_PERCENT_LOADED = 'RECEIVE_PERCENT_LOADED'; // network data
export const RECEIVE_PERCENT_PROCESSED = 'RECEIVE_PERCENT_PROCESSED'; // local client processing
export const RECEIVE_SOURCE_FILE = 'RECEIVE_SOURCE_FILE';
export const RECEIVE_SOURCE_URL = 'RECEIVE_SOURCE_URL';
export const RESET_UPLOAD = 'RESET_UPLOAD';
export const START_UPLOADED_INDEX = 'START_UPLOADED_INDEX';
export const FINISH_UPLOADED_INDEX = 'FINISH_UPLOADED_INDEX';
export const START_DRAG = 'START_DRAG';
export const FINISH_DRAG = 'FINISH_DRAG';
export const RECEIVE_AUDIO_BUFFER = 'RECEIVE_AUDIO_BUFFER';
export const RECEIVE_BACKGROUND_TRACKS = 'RECEIVE_BACKGROUND_TRACKS';
export const BACKGROUND_TRACK_FETCH_METADATA_COMPLETE =
  'BACKGROUND_TRACK_FETCH_METADATA_COMPLETE';
export const TRANSITIONS_FETCH_METADATA_COMPLETE =
  'TRANSITIONS_FETCH_METADATA_COMPLETE';
export const RECEIVE_TRANSITIONS = 'RECEIVE_TRANSITIONS';

export const CLIPPER_UPLOAD_FORM = 'clipperUpload';
export const CLIPPER_UPLOAD_SEGMENTS_FORM = 'clipperUploadSegments';
export const CLIPPER_SUPPORT_URL =
  'https://help.anchor.fm/hc/en-us/articles/115001185452';

const MAX_CLIP_DURATION_MINUTES = 90;
const MAX_CLIP_DURATION_SECONDS = MAX_CLIP_DURATION_MINUTES * 60;
const FILE_ENCODING = 'application/octet-binary';

const emptyArray = [];
const emptyObject = {};

const initialState = {
  backgroundTracks: emptyArray,
  audioBufferCache: emptyObject,
  backgroundTracksReadyToPlay: false,
  uploadingIndex: -1,
  uploadedIndex: -1,
  percentLoaded: 0,
  percentProcessed: 0,
  context: null,
  error: null,
  isDraggingFile: false,
  sourceFile: null,
  sourceUrl: null,
  audio: null, // audioId for special processing (signify you are using anchor-sourced content)
  doIncludeAttribution: false,
  transitions: emptyArray,
  transitionsReadyToPlay: false,
  showSchedulingView: false,
};

// reducer

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case CHANGE: {
      const {
        payload,
        meta: { form, field },
      } = action;
      if (
        (form === CLIPPER_UPLOAD_FORM ||
          form === CLIPPER_UPLOAD_SEGMENTS_FORM) &&
        field === 'publishImmediately'
      ) {
        return {
          ...state,
          showSchedulingView: !payload, // 'not' publishing immediately
        };
      }
      return state;
    }
    case RECEIVE_CONTEXT: {
      const { context } = action;
      return {
        ...state,
        context,
      };
    }
    case CLEAR_ERROR: {
      return {
        ...state,
        error: null,
      };
    }
    case RECEIVE_ERROR: {
      const { error } = action;
      return {
        ...state,
        error,
      };
    }
    case RECEIVE_PERCENT_LOADED: {
      const { percentLoaded } = action;
      return {
        ...state,
        percentLoaded: parseInt(percentLoaded, 10),
      };
    }
    case RECEIVE_PERCENT_PROCESSED: {
      const { percentProcessed } = action;
      return {
        ...state,
        percentProcessed: parseInt(percentProcessed, 10),
      };
    }
    case RESET_UPLOAD: {
      const { index } = action;
      return {
        ...state,
        uploadingIndex: -1,
        uploadedIndex: -1,
        showSchedulingView: false,
      };
    }
    case START_UPLOADED_INDEX: {
      const { index } = action;
      return {
        ...state,
        uploadingIndex: index,
      };
    }
    case FINISH_UPLOADED_INDEX: {
      const { index } = action;
      return {
        ...state,
        uploadedIndex: index,
      };
    }
    case START_DRAG:
      return {
        ...state,
        isDraggingFile: true,
      };
    case FINISH_DRAG:
      return {
        ...state,
        isDraggingFile: false,
      };
    case RECEIVE_SOURCE_FILE: {
      const { sourceFile } = action;
      return {
        ...state,
        sourceFile,
      };
    }
    case RECEIVE_SOURCE_URL: {
      const { sourceUrl, doIncludeAttribution, audio } = action;
      return {
        ...state,
        sourceUrl,
        doIncludeAttribution,
        audio,
      };
    }
    case RECEIVE_BACKGROUND_TRACKS: {
      const { backgroundTracks } = action;
      return {
        ...state,
        backgroundTracks,
      };
    }
    case RECEIVE_TRANSITIONS: {
      const { transitions } = action;
      return {
        ...state,
        transitions,
      };
    }
    case RECEIVE_AUDIO_BUFFER: {
      const { audioBufferCache } = state;
      const { url, buffer } = action;
      const updatedBackgroundTrackBufferCache = {
        ...audioBufferCache,
      };
      updatedBackgroundTrackBufferCache[url] = buffer;
      return {
        ...state,
        audioBufferCache: updatedBackgroundTrackBufferCache,
      };
    }
    case BACKGROUND_TRACK_FETCH_METADATA_COMPLETE:
      return {
        ...state,
        backgroundTracksReadyToChoose: true,
      };
    case TRANSITIONS_FETCH_METADATA_COMPLETE:
      return {
        ...state,
        transitionsReadyToChoose: true,
      };
    default:
      return state;
  }
}

// action creators

export function receivePercentLoaded(percentLoaded) {
  return {
    type: RECEIVE_PERCENT_LOADED,
    percentLoaded,
  };
}

export function receivePercentProcessed(percentProcessed) {
  return {
    type: RECEIVE_PERCENT_PROCESSED,
    percentProcessed,
  };
}

export function receiveContext(context) {
  return {
    type: RECEIVE_CONTEXT,
    context,
  };
}

export function clearError(error) {
  return {
    type: CLEAR_ERROR,
  };
}

export function receiveError(error) {
  return {
    type: RECEIVE_ERROR,
    error,
  };
}

export function resetUpload() {
  return {
    type: RESET_UPLOAD,
  };
}

function startSegmentUpload(index) {
  return {
    type: START_UPLOADED_INDEX,
    index,
  };
}

function finishSegmentUpload(index) {
  return {
    type: FINISH_UPLOADED_INDEX,
    index,
  };
}

export function startDrag() {
  return {
    type: START_DRAG,
  };
}
export function finishDrag() {
  return {
    type: FINISH_DRAG,
  };
}

function receiveSourceUrl(sourceUrl, doIncludeAttribution = false, audio) {
  return {
    type: RECEIVE_SOURCE_URL,
    sourceUrl,
    doIncludeAttribution,
    audio,
  };
}

function receiveBackgroundTracks(_backgroundTracks) {
  const backgroundTracks = _backgroundTracks.sort(sortAlphaByName);
  return {
    type: RECEIVE_BACKGROUND_TRACKS,
    backgroundTracks,
  };
}

function receiveAudioBuffer(url, buffer) {
  return {
    type: RECEIVE_AUDIO_BUFFER,
    url,
    buffer,
  };
}

function backgroundTrackFetchMetadataComplete() {
  return {
    type: BACKGROUND_TRACK_FETCH_METADATA_COMPLETE,
  };
}

function transitionFetchMetadataComplete() {
  return {
    type: TRANSITIONS_FETCH_METADATA_COMPLETE,
  };
}

function receiveTransitions(_transitions) {
  const transitions = _transitions.sort(sortAlphaByName);
  return {
    type: RECEIVE_TRANSITIONS,
    transitions,
  };
}

function transitionsDownloadComplete() {
  return {
    type: TRANSITIONS_DOWNLOAD_COMPLETE,
  };
}

function receiveSourceFile(sourceFile) {
  return {
    type: RECEIVE_SOURCE_FILE,
    sourceFile,
  };
}

// thunk actions

export function fetchUrl(_url, doIncludeAttribution = false, audio) {
  const url = pipeUrl(_url);
  return (dispatch, getState) => {
    // showProcessingModal('Loading audio...');

    const request = new XMLHttpRequest();
    request.open('GET', url, true);
    request.responseType = 'arraybuffer';

    request.onload = function() {
      let context;
      if (request.status === 200) {
        dispatch(
          receiveSourceFile(
            exportArrayBufferToFile(request.response, FILE_ENCODING)
          )
        );
        dispatch(receiveContext(createAudioContext()));
        dispatch(decodeBufferFromResponse(request.response))
          .then(success => {
            // transition to select state by URL
            dispatch(push('/clipper/select'));
          })
          .catch(err => {
            dispatch(
              receiveError({
                message: err && err.message,
                title: 'Upload Error',
              })
            );
          })
          .finally(() => {
            dispatch(receivePercentLoaded(100));
          });
      } else if (request.status === 300) {
        dispatch(receivePercentLoaded(100));
        alert('POPULATE RSS TABLE!');
        /*
        // RSS options:
        var optionsArrStr;
        if (request.response instanceof ArrayBuffer) {
        optionsArrStr = new TextDecoder('utf-8').decode(request.response);
        } else {
        optionsArrStr = request.response;
        }

        var optionsArr = JSON.parse(optionsArrStr);

        $rssSelectTable.html('');

        for (var r = 0; r < optionsArr.length; r++) {
        var $row = $('<li class="rssselect-option-row">' + optionsArr[r].title + '</li>');

        (function (url) {
        $row.on('click', function () {
        pipeUrl(url);
        });
        })(optionsArr[r].url);

        $rssSelectTable.append($row);
        }

        switchState(STATE.RSS_SELECT);
        */
      } else {
        let errStr;
        if (request.response instanceof ArrayBuffer) {
          errStr = new TextDecoder('utf-8').decode(request.response);
        } else {
          errStr = request.response;
        }
        dispatch(
          receiveError({
            title: 'Upload Error',
            subTitle:
              'The URL you tried to upload was not supported. Please try a different URL.',
            message: errStr,
          })
        );
      }
    };

    request.onprogress = function(evt) {
      if (evt.lengthComputable) {
        const percentComplete = (evt.loaded / evt.total) * 0.9;
        dispatch(receivePercentLoaded(100 * (percentComplete + 0.1)));
        // animateProgress($processingModeProgressBarCompleted, percentComplete + 0.1);
      }
    };

    request.onerror = function(err) {
      // usually a CORS issue
      dispatch(
        receiveError({
          title: 'Upload Error',
          subTitle:
            'There was a problem fetching your URL. Please try a different URL.',
          message: err && err.message,
        })
      );
    };

    dispatch(receiveSourceUrl(_url, doIncludeAttribution, audio));
    // Always animate the first 10% so it looks like something is happening
    // animateProgress($processingModeProgressBarCompleted, 0.1);
    dispatch(receivePercentLoaded(10));
    return request.send();
  };
}

/**
 * We only accept a single file via upload, but inputs are arrays
 */
export function fetchFile(acceptedFiles, rejectedFiles) {
  const file = acceptedFiles[0];
  const invalidFileSubTitle =
    'The file you tried to upload was invalid. Please check the file type and try again.';
  return (dispatch, getState) => {
    // initial feedback
    dispatch(receivePercentProcessed(10));
    dispatch(finishDrag());

    if (!file || rejectedFiles.length) {
      let message;
      if (rejectedFiles.length) {
        message = rejectedFiles.join(', ');
      }
      dispatch(
        receiveError({
          title: 'Upload Error',
          subTitle: invalidFileSubTitle,
          message,
        })
      );
    }
    const reader = new FileReader();

    reader.onload = function(ev) {
      const estimateForDecodingDuration = file.size / (1024 * 1024);
      let secondCount = 0;

      const interval = setInterval(() => {
        if (secondCount > estimateForDecodingDuration) {
          return;
        }

        const newPercent =
          0.5 + (secondCount / estimateForDecodingDuration) * 0.4;

        dispatch(receivePercentProcessed(100 * newPercent));
        // animateProgress($processingModeProgressBarCompleted, newPercent);

        secondCount++;
      }, 300);

      dispatch(receiveContext(createAudioContext()));
      dispatch(decodeBufferFromResponse(ev.target.result))
        .then(success => {
          // transition to select state by URL
          dispatch(push('/clipper/select'));
        })
        .catch(err => {
          dispatch(
            receiveError({
              message: err && err.message,
              title: 'Upload Error',
              subTitle: invalidFileSubTitle,
            })
          );
        })
        .finally(() => {
          clearInterval(interval);
          dispatch(receivePercentProcessed(100));
        });
    };

    reader.onprogress = function(evt) {
      if (evt.lengthComputable) {
        const percentComplete = (evt.loaded / evt.total) * 0.4;
        dispatch(receivePercentProcessed(100 * (percentComplete + 0.1)));
        // animateProgress($processingModeProgressBarCompleted, percentComplete + 0.1);
      }
    };

    dispatch(receiveSourceUrl(null)); // in case of repeat uploads
    dispatch(receiveSourceFile(file));
    reader.readAsArrayBuffer(file);
  };
}

function decodeBufferFromResponse(response) {
  return (dispatch, getState) => {
    const { clipper } = getState();
    return new Promise((resolve, reject) => {
      clipper.context.decodeAudioData(
        response,
        buffer => {
          if (buffer.duration && buffer.duration > MAX_CLIP_DURATION_SECONDS) {
            const err = new Error();
            err.message = `This clip is over ${MAX_CLIP_DURATION_MINUTES} minutes. Please choose a shorter one.`;
            reject(err);
            return;
          }
          dispatch(updateBuffer(buffer));
          dispatch(clearSegments());
          dispatch(setSubmitted(false));
          resolve(buffer);
        },
        err => {
          reject(err);
        }
      );
    });
  };
}

export function publishToStation(data) {
  return (dispatch, getState) => {
    // set title for all segments from form data
    data.segments.forEach(s => {
      dispatch(updateTitle(s.title, s.segmentKey));
    });
    const {
      clipper: { context, audioBufferCache, audio },
      clipSelection: { segments, buffer },
    } = getState();
    const buffers = getBuffersFromSegments(
      segments,
      context,
      buffer,
      audioBufferCache
    );
    const { attributionDescription, attributionUrl, publishDate } = data;
    return (
      new Promise((resolve, reject) => {
        if (!segments.length) {
          return reject({ message: 'No segments clipped' });
        }

        let i = 0;
        (function requestPublishNextSegment(segment, sourceAudio) {
          const segmentData = data.segments[i];
          dispatch(receivePercentLoaded(0)); // note also resets animation
          return dispatch(
            requestPublishSegment(
              segment,
              attributionDescription,
              attributionUrl,
              segmentData && segmentData.isAddedToClipBank,
              segmentData && segmentData.isAddedToStation,
              publishDate,
              sourceAudio,
              i
            )
          )
            .then(() => {
              i++;
              if (i === segments.length) {
                return resolve();
              }
              return requestPublishNextSegment({
                ...segments[i],
                buffer: buffers[i],
              });
            })
            .catch(
              err =>
                // TODO: "resume" / skip already uploaded in an error case
                reject(err) // rejects outer promise to stop count
            );
        })(
          {
            ...segments[i],
            buffer: buffers[i],
          },
          audio // pass audio into the first segment uploaded
        );
      })
        // .then(() => dispatch(push('/clipper/share')))
        .catch(err => {
          dispatch(
            receiveError({
              title: '😞',
              subTitle: 'Whoops, upload failed. Our bad.',
              message: err.message,
              dismissMessage: 'Retry?',
            })
          );
        })
    );
  };
}

export function startUploadAndSplitProcess(data) {
  return (dispatch, getState) => {
    const {
      clipper: { audio, sourceFile },
    } = getState();
    const { attributionDescription, attributionUrl, title, publishDate } = data;
    const caption = title;

    const formData = new FormData();
    formData.append('file', sourceFile, processFileName(sourceFile.name));

    formData.append('caption', caption);
    if (attributionDescription && attributionDescription.length > 0) {
      formData.append('attributionDescription', attributionDescription);
    }
    if (attributionUrl && attributionUrl.length > 0) {
      formData.append('attributionUrl', attributionUrl);
    }
    if (audio) {
      formData.append('sourceAudio', audio);
    }
    if (publishDate) {
      formData.append('publishDate', publishDate);
    }

    dispatch(startSegmentUpload(0));
    dispatch(receivePercentLoaded(0)); // note also resets animation
    nextFrame(() => {
      dispatch(receivePercentLoaded(10));
    });

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener(
        'progress',
        evt => {
          if (evt.lengthComputable) {
            const percentComplete = evt.loaded / evt.total;
            // Half of progress is uploading (rest is processing, upload to s3, etc):
            dispatch(receivePercentLoaded(100 * (percentComplete * 0.5)));
          }
        },
        false
      );
      xhr.open('POST', '/api/clip/station');

      xhr.onload = function onXhrLoad(data) {
        dispatch(receivePercentLoaded(100));
        if (xhr.status === 200) {
          // wait 600ms to allow progress animation to 100%
          return setTimeout(() => {
            resolve(data);
          }, 600);
        }
        return reject(new Error(xhr.status));
      };

      xhr.onerror = function(err) {
        // TODO: new error state. (usually CORS problem)
        return reject(err);
      };

      return xhr.send(formData);
    })
      .then(() => dispatch(finishSegmentUpload(0)))
      .catch(err => {
        dispatch(
          receiveError({
            title: '😞',
            subTitle: 'Whoops, upload failed. Our bad.',
            message: err.message,
            dismissMessage: 'Retry?',
          })
        );
      });
  };
}

export function fetchBackgroundTracks() {
  return (dispatch, getState) =>
    fetch('/api/clip/backgroundtracks', {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        return response;
      })
      .then(responseJson => {
        if (responseJson.status && responseJson.status !== 200) {
          console.log('Error');
          return;
        }
        const { backgroundTracks } = responseJson;
        const {
          clipper: { audioBufferCache, context },
        } = getState();
        // display dropdown(s)
        dispatch(backgroundTrackFetchMetadataComplete());
        dispatch(receiveBackgroundTracks(backgroundTracks));
      });
}

export function downloadAudioForPreview(url) {
  return (dispatch, getState) => {
    // return early if track alroady downloaded
    const {
      clipper: { context, audioBufferCache },
    } = getState();
    if (audioBufferCache[url]) {
      return;
    }
    return loadAllAudiosToCache([{ url }], audioBufferCache, context, dispatch);
  };
}

export function downloadAudioForPreviewByTransitionAudioId(transitionAudioId) {
  return (dispatch, getState) => {
    // return early if track alroady downloaded
    const {
      clipper: { context, audioBufferCache, transitions },
    } = getState();
    const { url } = transitions.find(t => transitionAudioId === t.audioId);
    if (audioBufferCache[url]) {
      return;
    }
    return loadAllAudiosToCache([{ url }], audioBufferCache, context, dispatch);
  };
}

export function fetchTransitions() {
  return (dispatch, getState) =>
    fetch('/api/clip/transitions', {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        return response;
      })
      .then(responseJson => {
        if (responseJson.status && responseJson.status !== 200) {
          console.log('Error');
          return;
        }
        const { transitions } = responseJson;
        const {
          clipper: { audioBufferCache, context },
        } = getState();
        // display dropdown(s)
        dispatch(transitionFetchMetadataComplete());
        dispatch(receiveTransitions(transitions));
      });
}

// misc

function getBuffersFromSegments(
  segments,
  context,
  buffer,
  audioBufferCache = {}
) {
  const buffers = [];
  segments.forEach(segment => {
    const { start, stop, doFade, backgroundTrackUrl } = segment;
    buffers.push(
      trimAndFade(
        context,
        buffer,
        start,
        stop,
        doFade,
        audioBufferCache[backgroundTrackUrl]
      )
    );
  });
  return buffers;
}

function loadAllAudiosToCache(audioObjects, bufferCache, context, dispatch) {
  const loadPromises = [];
  if (audioObjects && audioObjects.length) {
    for (let i = 0; i < audioObjects.length; i++) {
      loadPromises.push(
        loadAudioDataToCacheWithContext(
          audioObjects[i].url,
          bufferCache,
          context,
          dispatch
        )
      );
    }
  }

  return Promise.all(loadPromises);
}

function loadAudioDataToCacheWithContext(url, bufferCache, context, dispatch) {
  return new Promise((resolve, reject) => {
    if (bufferCache[url]) {
      return resolve(bufferCache[url]);
    }
    const xhr = new XMLHttpRequest();
    // xhr.open('GET', url, true);
    xhr.open('GET', url, true);
    xhr.responseType = 'arraybuffer';
    xhr.onload = function() {
      return context
        ? context.decodeAudioData(xhr.response, resolve, reject)
        : null;
    };
    xhr.onerror = reject;
    return xhr.send();
  }).then(buffer => {
    dispatch(receiveAudioBuffer(url, buffer));
  });
}

function requestPublishSegment(
  { title, buffer, segmentKey, transitionAudioId },
  attributionDescription = null,
  attributionUrl = null,
  isAddedToClipBank = false,
  isAddedToStation = false,
  publishDate = null,
  sourceAudio,
  index
) {
  return (dispatch, getState) => {
    const trimmedBuff = buffer;
    const caption = title;
    return new Promise((resolve, reject) => {
      dispatch(startSegmentUpload(index));
      if (!trimmedBuff) {
        return reject(new Error('Not trimmed'));
      }

      // Always animate the first 10% so it looks like something is happening
      // animateProgress($processingModeProgressBarCompleted, 0.1);
      // nextFrame so the any preceding zero percent kills transitions
      nextFrame(() => {
        dispatch(receivePercentLoaded(10));
      });

      /*
      if (!trimmedBuff.duration || trimmedBuff.duration < 0 || trimmedBuff.duration > MAX_DURATION) {
        return;
      }
      */

      // showProcessingModal('Clipping and shipping...');

      const exp = new Exporter(trimmedBuff);
      const blob = exp.export(FILE_ENCODING);

      // var caption = $captionTextarea.val();

      const formData = new FormData();
      formData.append(
        'file',
        blob,
        `${Math.random()
          .toString(16)
          .substr(2)}.wav`
      );
      if (caption && caption.length > 0) {
        formData.append('caption', caption);
      }
      if (transitionAudioId) {
        formData.append('postRecordingAudioId', transitionAudioId);
      }
      if (attributionDescription && attributionDescription.length > 0) {
        formData.append('attributionDescription', attributionDescription);
      }
      if (attributionUrl && attributionUrl.length > 0) {
        formData.append('attributionUrl', attributionUrl);
      }
      if (isAddedToClipBank) {
        formData.append('isAddedToClipBank', true);
      }
      if (isAddedToStation) {
        formData.append('isAddedToStation', true);
      }
      if (publishDate) {
        formData.append('publishDate', publishDate);
      }
      if (sourceAudio) {
        formData.append('sourceAudio', sourceAudio);
      }

      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener(
        'progress',
        evt => {
          if (evt.lengthComputable) {
            const percentComplete = evt.loaded / evt.total;
            // Half of progress is uploading:
            // animateProgress($processingModeProgressBarCompleted, percentComplete * 0.5);
            dispatch(receivePercentLoaded(100 * (percentComplete * 0.5)));
          }
        },
        false
      );
      xhr.open('POST', '/api/clip/station/segment');

      xhr.onload = function onXhrLoad(data) {
        let context;

        dispatch(receivePercentLoaded(100));
        if (xhr.status === 200) {
          // TODO: rethink embed preview and share buttons for entire station instead of clip
          /*
          var embedData = '<iframe src="' + data.embedUrl + '" height="210px" width="540px" frameborder="0" scrolling="no"></iframe>';

          outputClipId = data.clipId;

          $postPublishingShareRowInputEmbed.val(embedData);
          $postPublishingShareRowInputLink.val(data.webUrl);
          $shareToTwitterLink.attr('href', 'https://twitter.com/intent/tweet?text=Listen%20to%20"' + encodeURIComponent(caption) + '" by ' + encodeURIComponent(userName) + ' ⚓ ' + data.webUrl);
          $shareToFacebookLink.attr('href', 'https://www.facebook.com/dialog/share' +
                              '?app_id=446611785530020' +
                              '&href=' + data.webUrl +
                              '&redirect_uri=' + data.webUrl
                      );

          uploadClipImageCanvas(function (photoErr) {
            // Currently photo upload errors are ignored

            animateProgress($processingModeProgressBarCompleted, 1, function () {
              switchState(STATE.POST_PUBLISHING);
            });
          });
          */
          // wait 600ms to allow progress animation to 100%
          return setTimeout(() => {
            resolve(data);
          }, 600);
        }
        return reject(new Error(xhr.status));
      };

      xhr.onerror = function(err) {
        // TODO: new error state. (usually CORS problem)
        return reject(err);
      };

      return xhr.send(formData);
    }).then(() => dispatch(finishSegmentUpload(index)));
  };
}

export function convertSecondsToMinutes(seconds) {
  function padLeft(s) {
    return (new Array(3).join('0') + s).slice(-2);
  }

  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;

  return `${m}:${padLeft(s)}`;
}

function pipeUrl(url) {
  return `/api/file?url=${encodeURIComponent(url)}`;
}

function sortAlphaByName(a, b) {
  const nameA = a.name.toUpperCase();
  const nameB = b.name.toUpperCase();
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }
  return 0;
}

function exportArrayBufferToFile(arrayBuffer, type) {
  const blob = new Blob([arrayBuffer], { type });
  return blob;
}

function processFileName(fileName) {
  if (!fileName) {
    return `${Math.random()
      .toString(16)
      .substr(2)}.wav`;
  }
  return fileName.replace(/[^a-zA-Z0-9-\.]/g, '-');
}

export function configRoundToNextHour(moment) {
  return function roundToNextHour(value) {
    const momentVal = moment(value);
    if (
      momentVal.milliseconds() !== 0 ||
      momentVal.seconds() !== 0 ||
      momentVal.minutes() !== 0
    ) {
      return momentVal.add(1, 'hours').startOf('hour');
    }
    return momentVal;
  };
}
